import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Play, Star, ChevronRight, Upload, Video, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function HitsGalleryPage() {
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHit, setSelectedHit] = useState(null);

  useEffect(() => {
    const fetchHits = async () => {
      try {
        const response = await axios.get(`${API}/hits-gallery`);
        setHits(response.data);
      } catch (error) {
        console.error('Error fetching hits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHits();
  }, []);

  // Placeholder hits for when gallery is empty
  const placeholderHits = [
    {
      hit_id: 'placeholder_1',
      title: 'Tom Brady Jersey Pull!',
      description: 'Incredible hit from Pure Shmacks Series 1!',
      athlete_name: 'Tom Brady',
      product_name: 'Pure Shmacks Multi Sport Premium Autographed Jerseys',
      featured: true,
      video_url: null,
      image_url: 'https://images.unsplash.com/photo-1571461542309-dedaaec54069?w=800'
    },
    {
      hit_id: 'placeholder_2',
      title: 'Patrick Mahomes Mini Helmet',
      description: 'Hot Headz delivers again!',
      athlete_name: 'Patrick Mahomes',
      product_name: 'Hot Headz Autographed Mini Helmets',
      featured: true,
      video_url: null,
      image_url: 'https://images.unsplash.com/photo-1632166160997-90bdd7c41e97?w=800'
    },
    {
      hit_id: 'placeholder_3',
      title: 'LeBron James Jersey',
      description: 'Multi-sport magic!',
      athlete_name: 'LeBron James',
      product_name: 'Pure Shmacks Multi Sport Premium Autographed Jerseys',
      featured: false,
      video_url: null,
      image_url: 'https://images.unsplash.com/photo-1610902773655-a67040ead1cb?w=800'
    }
  ];

  const displayHits = hits.length > 0 ? hits : placeholderHits;
  const featuredHits = displayHits.filter(h => h.featured);
  const recentHits = displayHits.filter(h => !h.featured);

  return (
    <div className="pt-20 min-h-screen" data-testid="hits-gallery-page">
      {/* Hero Section */}
      <section className="relative py-20 hero-glow border-b border-[#27272A]">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="label-caps text-[#C41E3A] mb-4 block">Real Pulls, Real Excitement</span>
            <h1 className="heading-display text-4xl md:text-6xl text-white mb-6" data-testid="hits-gallery-title">
              Hits Gallery
            </h1>
            <p className="text-lg md:text-xl text-white/70 mb-8">
              Watch real unboxings and see the incredible hits our collectors have pulled. 
              Could you be next?
            </p>
            
            <Link to="/mystery-boxes">
              <Button className="btn-primary flex items-center gap-2">
                Shop Mystery Boxes <ChevronRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Hits */}
      {featuredHits.length > 0 && (
        <section className="section-gap" data-testid="featured-hits">
          <div className="container-main">
            <div className="flex items-center gap-3 mb-10">
              <Star className="w-6 h-6 text-yellow-500" />
              <h2 className="heading-display text-3xl text-white">Featured Hits</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredHits.map((hit) => (
                <HitCard key={hit.hit_id} hit={hit} onClick={() => setSelectedHit(hit)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Hits */}
      <section className="section-gap bg-[#0A0A0A]" data-testid="recent-hits">
        <div className="container-main">
          <h2 className="heading-display text-3xl text-white mb-10">Recent Pulls</h2>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="aspect-video bg-[#111111] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {(recentHits.length > 0 ? recentHits : displayHits).map((hit) => (
                <HitCard key={hit.hit_id} hit={hit} onClick={() => setSelectedHit(hit)} compact />
              ))}
            </div>
          )}

          {displayHits.length === 0 && !loading && (
            <div className="text-center py-12">
              <Video className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="heading-display text-xl text-white mb-2">No Hits Yet</h3>
              <p className="text-white/60">Check back soon for unboxing videos!</p>
            </div>
          )}
        </div>
      </section>

      {/* Upload CTA */}
      <section className="py-16 border-t border-[#27272A]">
        <div className="container-main">
          <div className="bg-[#111111] border border-[#27272A] p-8 md:p-12 text-center max-w-2xl mx-auto">
            <Upload className="w-12 h-12 text-[#C41E3A] mx-auto mb-4" />
            <h3 className="heading-display text-2xl text-white mb-4">Got a Big Hit?</h3>
            <p className="text-white/60 mb-6">
              Share your unboxing video with us! Tag us on social media or contact us to be featured in the gallery.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <Button className="btn-secondary">@PureShmacks on Instagram</Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {selectedHit && (
        <HitModal hit={selectedHit} onClose={() => setSelectedHit(null)} />
      )}
    </div>
  );
}

function HitCard({ hit, onClick, compact = false }) {
  const hasVideo = !!hit.video_url;

  return (
    <div
      onClick={onClick}
      className={`group relative bg-[#111111] border border-[#27272A] overflow-hidden cursor-pointer hover:border-[#C41E3A] transition-colors duration-300 ${compact ? '' : ''}`}
      data-testid={`hit-card-${hit.hit_id}`}
    >
      {/* Image/Video Thumbnail */}
      <div className={`relative ${compact ? 'aspect-video' : 'aspect-[4/3]'} bg-[#0A0A0A]`}>
        <img
          src={hit.image_url || 'https://images.unsplash.com/photo-1571461542309-dedaaec54069?w=800'}
          alt={hit.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Play Button Overlay */}
        {hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-16 h-16 rounded-full bg-[#C41E3A] flex items-center justify-center">
              <Play className="w-8 h-8 text-white ml-1" />
            </div>
          </div>
        )}

        {/* Video Badge */}
        {hasVideo && (
          <Badge className="absolute top-3 left-3 bg-[#C41E3A] text-white font-barlow uppercase text-xs px-2 py-1">
            <Video className="w-3 h-3 mr-1" />
            Video
          </Badge>
        )}

        {/* Featured Badge */}
        {hit.featured && (
          <Badge className="absolute top-3 right-3 bg-yellow-500 text-black font-barlow uppercase text-xs px-2 py-1">
            <Star className="w-3 h-3 mr-1" />
            Featured
          </Badge>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-barlow font-bold text-white text-lg mb-1 group-hover:text-[#C41E3A] transition-colors duration-300">
          {hit.title}
        </h3>
        {hit.athlete_name && (
          <p className="text-[#C41E3A] font-medium text-sm mb-2">{hit.athlete_name}</p>
        )}
        {hit.product_name && !compact && (
          <p className="text-white/50 text-sm">{hit.product_name}</p>
        )}
      </div>
    </div>
  );
}

function HitModal({ hit, onClose }) {
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#0A0A0A] border border-[#27272A] max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Video/Image */}
        <div className="aspect-video bg-black">
          {hit.video_url ? (
            <video
              src={hit.video_url}
              controls
              autoPlay
              className="w-full h-full"
            />
          ) : (
            <img
              src={hit.image_url || 'https://images.unsplash.com/photo-1571461542309-dedaaec54069?w=800'}
              alt={hit.title}
              className="w-full h-full object-contain"
            />
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <h2 className="font-barlow font-black text-2xl text-white mb-2">{hit.title}</h2>
          {hit.athlete_name && (
            <p className="text-[#C41E3A] font-bold text-lg mb-4">{hit.athlete_name}</p>
          )}
          {hit.description && (
            <p className="text-white/70 mb-4">{hit.description}</p>
          )}
          {hit.product_name && (
            <Badge className="bg-[#27272A] text-white/70 font-barlow text-sm px-3 py-1">
              {hit.product_name}
            </Badge>
          )}

          <div className="mt-6 flex gap-4">
            <Button onClick={onClose} className="btn-secondary">
              Close
            </Button>
            <Link to="/mystery-boxes">
              <Button className="btn-primary">
                Shop Mystery Boxes
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
